import { generatePath } from 'react-router-dom';
import { transformPaginatedResponseFilters } from '../../../components/paginated-list/helper';
import { API_URLS } from '../../../routes/urls';
import { ProgramListItem } from '../../../types/program';
import { PaginatedListParams, PaginatedListResponse, StrOrNum } from '../../../types/util';
import { convertObjectToFormData } from '../../../utils/form';
import { apiSlice } from '../../apiSlice';
import { QueryErrorReturnValue } from '../../types';
import { addSnackbar } from '../snackbar/snackbarSlice';
import { EligibleProgramIdsResponse, GetSingleProgramPayload, GetSingleProgramResponse } from './types';

const programApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listPrograms: builder.query<PaginatedListResponse<ProgramListItem>, PaginatedListParams | void>({
      query: (params) => ({
        url: API_URLS.programs,
        params: {
          ...params,
          limit: 12,
        },
      }),
      transformResponse: (response: PaginatedListResponse<ProgramListItem>) =>
        transformPaginatedResponseFilters(response),
    }),
    singleProgram: builder.query<GetSingleProgramResponse, GetSingleProgramPayload>({
      query: ({ instituteSlug, programSlug }) => ({
        url: generatePath(API_URLS.singleProgram, { instituteSlug, programSlug }),
      }),
    }),
    featuredPrograms: builder.query<ProgramListItem[], void>({
      query: () => ({
        url: API_URLS.featuredPrograms,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          dispatch(
            addSnackbar({
              message: err.error.message,
              severity: 'error',
            })
          );
        }
      },
    }),

    eligibleProgramIds: builder.query<EligibleProgramIdsResponse, PaginatedListParams>({
      query: (params) => ({
        url: API_URLS.eligibleProgramIds,
        params,
      }),
      transformResponse: (response: EligibleProgramIdsResponse) => transformPaginatedResponseFilters(response),
      providesTags: ['EligibleModule'],
    }),

    programsByIds: builder.query<ProgramListItem[], StrOrNum[]>({
      query: (ids) => ({
        url: API_URLS.programsByIds,
        method: 'POST',
        data: convertObjectToFormData({ ids }),
      }),
    }),
  }),
});

export const {
  useSingleProgramQuery,
  useListProgramsQuery,
  useFeaturedProgramsQuery,
  useEligibleProgramIdsQuery,
  useLazyProgramsByIdsQuery,
} = programApi;
